import { useEffect, useState, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";
import { BankDetailsCard } from "@/components/BankDetailsCard";
import { ChatWindow } from "@/components/ChatWindow";
import { useAuth } from "@/context/AuthContext";
import { useBusinessInfo } from "@/lib/businessInfo";
import api, { formatApiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { generateInvoicePDF } from "@/lib/pdf";
import { toast } from "sonner";
import { Download, Eye, IndianRupee, FileText, MessageSquare, Plus, Building2 } from "lucide-react";

import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

const StatCard = ({ label, value, sublabel, icon: Icon, testId }) => (
  <Card className="border-slate-200 shadow-none rounded-md p-6" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="label-tag">{label}</p>
        <p className="mt-3 font-mono text-3xl font-medium text-slate-900 tracking-tight">
          {value}
        </p>
        {sublabel && <p className="text-xs text-slate-500 mt-2">{sublabel}</p>}
      </div>
      {Icon && (
        <div className="w-9 h-9 flex items-center justify-center border border-slate-200 bg-slate-50 rounded-md">
          <Icon className="w-4 h-4 text-slate-700" />
        </div>
      )}
    </div>
  </Card>
);

export default function ClientDashboard() {
  const { user } = useAuth();
  const businessInfo = useBusinessInfo();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [unread, setUnread] = useState(0);
  const [unreadByClient, setUnreadByClient] = useState({});
  const linkedClients = useMemo(
    () => user?.linked_clients || [],
    [user?.linked_clients]
  );
  const [activeChatCid, setActiveChatCid] = useState(
    linkedClients[0]?.id || null
  );

  useEffect(() => {
    if (!activeChatCid && linkedClients.length > 0) {
      setActiveChatCid(linkedClients[0].id);
    }
  }, [linkedClients, activeChatCid]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bills/my");
        setBills(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Poll unread messages
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!cancelled) {
          setUnread(data.total || 0);
          setUnreadByClient(data.by_client || {});
        }
      } catch {
        // Polling error is transient; the next tick recovers. Intentionally silent.
      }
    };
    tick();
    const id = setInterval(tick, 6000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const totals = useMemo(() => {
    const t = { billed: 0, paid: 0, unpaid: 0 };
    bills.forEach((b) => {
      t.billed += b.total;
      if (b.status === "paid") t.paid += b.total;
      else t.unpaid += b.total;
    });
    return t;
  }, [bills]);

  return (
    <AppShell title={`Hello, ${user?.name?.split(" ")[0] || "there"}`} subtitle="Your invoices and payment history.">
      {/* Linked companies bar */}
      {linkedClients.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3 justify-between" data-testid="linked-companies-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="label-tag">Linked entities</span>
            {linkedClients.map((lc) => (
              <span
                key={lc.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-white border border-slate-200 rounded-sm"
                data-testid={`linked-entity-${lc.id}`}
              >
                <Building2 className="w-3 h-3 text-slate-400" />
                <span className="font-mono text-slate-500">{lc.client_code}</span>
                <span className="text-slate-900">{lc.name}</span>
              </span>
            ))}
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="border-slate-300"
            data-testid="link-another-client-btn"
          >
            <Link to="/link-client">
              <Plus className="w-3.5 h-3.5 mr-1" /> Link another Client ID
            </Link>
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="Total Billed"
          value={formatINR(totals.billed)}
          sublabel={`${bills.length} invoice${bills.length === 1 ? "" : "s"}`}
          icon={FileText}
          testId="client-stat-billed"
        />
        <StatCard
          label="Paid"
          value={formatINR(totals.paid)}
          icon={IndianRupee}
          testId="client-stat-paid"
        />
        <StatCard
          label="Outstanding"
          value={formatINR(totals.unpaid)}
          icon={IndianRupee}
          testId="client-stat-unpaid"
        />
      </div>

      {totals.unpaid > 0 && (
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(() => {
            const unpaidFirmKeys = Array.from(
              new Set(
                bills
                  .filter((b) => b.status === "unpaid")
                  .map((b) => b.firm || businessInfo?.default_firm)
                  .filter(Boolean)
              )
            );
            const firms = businessInfo?.firms || [];
            const shown = firms.filter((f) => unpaidFirmKeys.includes(f.key));
            if (shown.length === 0) {
              return <BankDetailsCard businessInfo={businessInfo} />;
            }
            return shown.map((f) => (
              <BankDetailsCard key={f.key} firm={f} />
            ));
          })()}
        </div>
      )}

      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-md p-1 h-11 mb-6">
          <TabsTrigger
            value="invoices"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4"
            data-testid="client-tab-invoices"
          >
            Invoices
          </TabsTrigger>
          <TabsTrigger
            value="messages"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4 relative"
            data-testid="client-tab-messages"
          >
            Messages
            {unread > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-rose-600 text-white"
                data-testid="client-unread-badge"
              >
                {unread}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <p className="label-tag">Invoices</p>
                <h3 className="font-display text-lg font-medium text-slate-900 mt-1">
                  Billing History
                </h3>
              </div>
            </div>

            <Table data-testid="client-bills-table">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="label-tag text-slate-500">Invoice</TableHead>
                  {linkedClients.length > 1 && (
                    <TableHead className="label-tag text-slate-500">Entity</TableHead>
                  )}
                  <TableHead className="label-tag text-slate-500">Date</TableHead>
                  <TableHead className="label-tag text-slate-500">Due</TableHead>
                  <TableHead className="label-tag text-slate-500 text-right">Amount</TableHead>
                  <TableHead className="label-tag text-slate-500">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={linkedClients.length > 1 ? 7 : 6} className="text-center text-slate-400 py-10">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && bills.length === 0 && (
              <TableRow>
                <TableCell colSpan={linkedClients.length > 1 ? 7 : 6} className="text-center text-slate-400 py-10">
                  No invoices yet.
                </TableCell>
              </TableRow>
            )}
            {!loading && bills.length > 0 && bills.map((b) => {
                const firmObj =
                  (businessInfo?.firms || []).find(
                    (f) => f.key === (b.firm || businessInfo?.default_firm)
                  );
                return (
                <TableRow key={b.id} data-testid={`client-bill-row-${b.id}`}>
                  <TableCell className="font-mono font-medium">
                    {b.invoice_no}
                    {firmObj && (businessInfo?.firms?.length || 0) > 1 && (
                      <div
                        className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal bg-slate-100 text-slate-600 border border-slate-200 rounded-sm"
                        data-testid={`client-bill-firm-${b.id}`}
                      >
                        {firmObj.name}
                      </div>
                    )}
                  </TableCell>
                  {linkedClients.length > 1 && (
                    <TableCell>
                      <div className="text-xs font-mono text-slate-500">{b.client_code}</div>
                      <div className="text-sm text-slate-900">{b.client_name}</div>
                    </TableCell>
                  )}
                  <TableCell className="text-slate-600">{formatDate(b.created_at)}</TableCell>
                  <TableCell className="text-slate-600">{formatDate(b.due_date)}</TableCell>
                  <TableCell className="font-mono text-right text-slate-900">
                    {formatINR(b.total)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={b.status} data-testid={`client-bill-status-${b.id}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelected(b)}
                        data-testid={`view-bill-${b.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => generateInvoicePDF(b, businessInfo)}
                        data-testid={`client-download-pdf-${b.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Card>
        </TabsContent>

        <TabsContent value="messages">
          {linkedClients.length > 1 ? (
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 h-[70vh] min-h-[500px]">
              <Card className="border-slate-200 shadow-none rounded-md overflow-hidden flex flex-col" data-testid="client-conversations-list">
                <div className="p-3 border-b border-slate-200">
                  <p className="label-tag">Your entities</p>
                </div>
                <div className="overflow-y-auto flex-1">
                  {linkedClients.map((lc) => {
                    const active = activeChatCid === lc.id;
                    const cnt = unreadByClient[lc.id] || 0;
                    return (
                      <button
                        key={lc.id}
                        type="button"
                        onClick={() => setActiveChatCid(lc.id)}
                        className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          active ? "bg-slate-100" : ""
                        }`}
                        data-testid={`client-thread-${lc.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm text-slate-900 truncate">
                            {lc.name}
                          </span>
                          {cnt > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-rose-600 text-white shrink-0">
                              {cnt}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                          {lc.client_code}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Card>
              {activeChatCid ? (
                <ChatWindow
                  key={activeChatCid}
                  clientId={activeChatCid}
                  currentRole="client"
                  className="h-full"
                />
              ) : (
                <div className="border border-slate-200 bg-white rounded-md flex items-center justify-center text-slate-400 text-sm">
                  <div className="text-center">
                    <MessageSquare className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                    Select an entity to open its conversation
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-[70vh] min-h-[500px]">
              <ChatWindow
                clientId={linkedClients[0]?.id}
                currentRole="client"
                className="h-full"
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* View Invoice Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="view-bill-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-medium text-2xl flex items-center justify-between">
                  <span>{selected.invoice_no}</span>
                  <StatusBadge status={selected.status} />
                </DialogTitle>
                <DialogDescription className="text-slate-500">
                  Issued {formatDate(selected.created_at)}
                  {selected.due_date && ` · Due ${formatDate(selected.due_date)}`}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2 pt-2">
                {selected.services.map((s, i) => (
                  <div
                    key={`${selected.id}-svc-${i}`}
                    className="flex justify-between text-sm border-b border-slate-100 pb-2"
                  >
                    <span className="text-slate-700">{s.description}</span>
                    <span className="font-mono text-slate-900">{formatINR(s.fee)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3 border-t border-slate-300">
                  <span className="label-tag">Total</span>
                  <span className="font-mono text-xl font-medium text-slate-900">
                    {formatINR(selected.total)}
                  </span>
                </div>
              </div>

              {selected.notes && (
                <p className="text-sm text-slate-500 border-t border-slate-100 pt-3">
                  <span className="label-tag block mb-1">Notes</span>
                  {selected.notes}
                </p>
              )}

              <div className="flex justify-end pt-4 border-t border-slate-100">
                <Button
                  onClick={() => generateInvoicePDF(selected, businessInfo)}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="modal-download-pdf"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
